import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useMemo, useState } from 'preact/hooks';
import { leadsApi } from '@/api/leads';
import { SignalBars } from '@/components/common/SignalBars';
import { IncludeNonAudienceToggle } from '@/components/common/IncludeNonAudienceToggle';
import { LeadSidePanel } from '@/components/common/LeadSidePanel';
import { PersonAvatar, CompanyAvatar } from '@/components/common/Avatar';
import { getWebTypeMeta } from '@/constants/webTypes';
import { timeFromNowVerbose } from '@/utils/time';
import { RefreshCcw } from 'lucide-preact';
import { getLeadComparator, getCompanyComparator } from '@/utils/leadSorting';
import { getLeadDisplayName, shouldShowEmailLine } from '@/utils/leadFormatting';
import { DataGrid } from '@/components/common/grids';
import { useCompany } from '@/hooks/useCompany';
import { LEAD_EXPORT_COLUMNS, COMPANY_EXPORT_COLUMNS, generateExportFilename } from '@/utils/leadExport';
// Render a host link (strips leading www.) falling back to full URL if parsing fails
function companyHostAnchor(companyUrl) {
    try {
        const host = new URL(companyUrl).hostname.replace(/^www\./, '');
        return _jsx("a", { href: companyUrl, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: companyUrl, children: host });
    }
    catch {
        return _jsx("a", { href: companyUrl, target: "_blank", rel: "noopener noreferrer", class: "hover:underline", title: companyUrl, children: companyUrl });
    }
}
export const WorkspaceLeads = ({ clientCompanyId, workspaceId }) => {
    const { company, currentWorkspace } = useCompany();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('contacts');
    const [includeNonAudience, setIncludeNonAudience] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);
    const EMPTY = { Leads: [], Companies: [], LeadCount: 0, CompanyCount: 0, GeneratedAtUtc: new Date().toISOString() };
    const load = (signal) => {
        setLoading(true);
        setError(null);
        leadsApi.getWorkspaceLeads(workspaceId, signal)
            .then(r => { const payload = r?.data; if (payload)
            setData(payload);
        else
            setData(EMPTY); })
            .catch(e => {
            if (e instanceof Error && e.name === 'AbortError')
                return;
            setError(e?.message || 'Unknown error');
            setData(EMPTY);
        })
            .finally(() => setLoading(false));
    };
    useEffect(() => {
        const abortController = new AbortController();
        load(abortController.signal);
        return () => abortController.abort();
    }, [workspaceId]);
    const generatedRelative = data ? timeFromNowVerbose(data.GeneratedAtUtc) : '—';
    const leads = data?.Leads || [];
    const companies = data?.Companies || [];
    const filteredLeads = mode === 'contacts' ? (includeNonAudience ? leads : leads.filter(l => l.IsAudienceTarget !== false)) : leads;
    useEffect(() => { if (selectedLead && !filteredLeads.some(l => l.LeadId === selectedLead.LeadId))
        setSelectedLead(null); }, [filteredLeads, selectedLead]);
    // ---------- Contact columns for DataGrid ----------
    const contactColumns = useMemo(() => [
        {
            key: 'lead',
            header: 'Lead',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (l) => {
                const name = getLeadDisplayName(l);
                const showEmail = shouldShowEmailLine(l, name);
                return (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(PersonAvatar, { firstName: l.FirstName, lastName: l.LastName, fullName: l.FullName, profilePicUrl: l.ProfilePicUrl, alt: name }), _jsxs("div", { class: "flex flex-col leading-tight min-w-0", children: [_jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]", title: name, children: name }), showEmail && _jsx("span", { class: "text-xs text-gray-500 dark:text-gray-400 truncate max-w-[200px]", title: l.Email, children: l.Email })] })] }));
            },
        },
        {
            key: 'company',
            header: 'Company',
            sortable: true,
            className: 'truncate max-w-[160px]',
            render: (l) => _jsx("span", { title: l.Company || '—', children: l.Company || '—' }),
        },
        {
            key: 'title',
            header: 'Title',
            sortable: true,
            className: 'truncate max-w-[180px]',
            render: (l) => _jsx("span", { title: l.JobTitle || '—', children: l.JobTitle || '—' }),
        },
        {
            key: 'types',
            header: 'Types',
            render: (l) => (_jsx("div", { class: "flex flex-wrap gap-1", children: l.WebTypes?.map(wt => {
                    const meta = getWebTypeMeta(wt);
                    return _jsx("span", { class: `inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${meta?.chipClass || 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200'}`, children: wt }, wt);
                }) })),
        },
        {
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(_Fragment, { children: l.LastInteractionAt ? timeFromNowVerbose(l.LastInteractionAt) : '—' }),
        },
        {
            key: 'conversions',
            header: 'Conversions',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(_Fragment, { children: l.InteractionCount }),
        },
        {
            key: 'signal',
            header: 'Workspace Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (l) => _jsx(SignalBars, { score: l.SignalScore }),
        },
    ], []);
    // ---------- Company columns for DataGrid ----------
    const companyColumns = useMemo(() => [
        {
            key: 'company',
            header: 'Company',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => (_jsxs("div", { class: "flex items-center gap-3", children: [_jsx(CompanyAvatar, { companyName: c.CompanyName, logoUrl: c.CompanyLogoUrl }), _jsx("div", { children: c.CompanyLinkedInUrl
                            ? _jsx("a", { href: c.CompanyLinkedInUrl, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyLinkedInUrl, children: c.CompanyName })
                            : c.CompanyWebsite
                                ? _jsx("a", { href: c.CompanyWebsite, target: "_blank", rel: "noopener noreferrer", class: "font-medium text-gray-900 dark:text-gray-100 hover:underline", title: c.CompanyWebsite, children: c.CompanyName })
                                : _jsx("span", { class: "font-medium text-gray-900 dark:text-gray-100", children: c.CompanyName }) })] })),
        },
        {
            key: 'url',
            header: 'URL',
            className: 'truncate max-w-[180px]',
            render: (c) => c.CompanyWebsite ? companyHostAnchor(c.CompanyWebsite) : _jsx(_Fragment, { children: '—' }),
        },
        {
            key: 'industry',
            header: 'Industry',
            sortable: true,
            className: 'truncate max-w-[160px]',
            render: (c) => _jsx("span", { title: c.Industry || '—', children: c.Industry || '—' }),
        },
        {
            key: 'size',
            header: 'Size',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.StaffCountRange || '—' }),
        },
        {
            key: 'lastConversion',
            header: 'Last Conversion',
            sortable: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(_Fragment, { children: c.LastConversionAt ? timeFromNowVerbose(c.LastConversionAt) : '—' }),
        },
        {
            key: 'leads',
            header: 'Leads',
            sortable: true,
            render: (c) => _jsx(_Fragment, { children: c.LeadCount }),
        },
        {
            key: 'signal',
            header: 'Workspace Signal',
            sortable: true,
            required: true,
            className: 'whitespace-nowrap',
            render: (c) => _jsx(SignalBars, { score: c.SignalScore }),
        },
    ], []);
    // ---------- Export configs for DataGrid ----------
    const contactExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Workspace', 'leads'),
        columns: LEAD_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    const companyExportConfig = useMemo(() => ({
        getFilename: () => generateExportFilename(company?.Name, currentWorkspace?.Name, 'Workspace', 'companies'),
        columns: COMPANY_EXPORT_COLUMNS.map(col => ({ header: col.label, getValue: col.getValue })),
    }), [company?.Name, currentWorkspace?.Name]);
    // Shared page header component
    const PageHeader = () => (_jsx("div", { class: "flex flex-col md:flex-row md:items-center justify-between gap-4", children: _jsxs("div", { class: "min-w-0", children: [_jsx("h1", { class: "text-2xl md:text-3xl font-semibold text-slate-900 dark:text-white tracking-tight truncate", children: "Workspace Leads" }), _jsx("p", { class: "text-slate-600 dark:text-slate-300", children: "All leads from within the workspace" })] }) }));
    const ModeSwitch = () => {
        const options = [
            { key: 'contacts', label: 'Contacts' },
            { key: 'companies', label: 'Companies' }
        ];
        return (_jsx("div", { role: "radiogroup", "aria-label": "Leads view mode", class: "inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden", children: options.map(o => {
                const active = o.key === mode;
                return (_jsx("button", { role: "radio", "aria-checked": active, onClick: () => !active && setMode(o.key), class: `px-3 py-1.5 text-xs font-medium transition focus:outline-none focus:z-10 ${active ? 'bg-green-600 text-white dark:bg-green-500' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`, children: o.label }, o.key));
            }) }));
    };
    const MetricsBar = () => (_jsxs("div", { class: "flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between mb-3", children: [_jsxs("div", { class: "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs sm:text-sm text-gray-600 dark:text-gray-300", children: [_jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Conversions:" }), " ", data?.LeadCount ?? '—'] }), _jsxs("span", { children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Companies:" }), " ", data?.CompanyCount ?? '—'] }), _jsxs("span", { class: "hidden xs:inline", children: [_jsx("span", { class: "font-semibold text-gray-900 dark:text-gray-100", children: "Generated:" }), " ", generatedRelative] }), mode === 'contacts' && (_jsx(IncludeNonAudienceToggle, { value: includeNonAudience, onToggle: () => setIncludeNonAudience(v => !v) }))] }), _jsxs("div", { class: "flex items-center gap-2", children: [_jsx(ModeSwitch, {}), _jsxs("button", { onClick: () => load(), class: "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700", title: "Refresh", children: [_jsx(RefreshCcw, { class: "w-4 h-4" }), "Refresh"] })] })] }));
    if (loading)
        return (_jsxs("div", { class: "px-6 pt-4 lg:pt-6 pb-6 space-y-6", children: [_jsx(PageHeader, {}), _jsxs("div", { class: "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4", children: [_jsx(MetricsBar, {}), _jsx("div", { class: "p-4 text-sm text-gray-500 dark:text-gray-400", children: "Loading..." })] })] }));
    if (error)
        return (_jsxs("div", { class: "px-6 pt-4 lg:pt-6 pb-6 space-y-6", children: [_jsx(PageHeader, {}), _jsxs("div", { class: "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4", children: [_jsx(MetricsBar, {}), _jsxs("div", { class: "p-4 text-sm text-red-600 dark:text-red-400", children: ["Failed: ", error] })] })] }));
    const showLeads = mode === 'contacts';
    const showCompanies = mode === 'companies';
    if (showLeads && filteredLeads.length === 0)
        return (_jsxs("div", { class: "px-6 pt-4 lg:pt-6 pb-6 space-y-6", children: [_jsx(PageHeader, {}), _jsxs("div", { class: "rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 p-6 text-center text-sm text-gray-600 dark:text-gray-400", children: [_jsx(MetricsBar, {}), _jsx("div", { class: "mt-6", children: includeNonAudience ? 'No leads yet.' : 'No audience leads yet.' })] })] }));
    if (showCompanies && companies.length === 0)
        return (_jsxs("div", { class: "px-6 pt-4 lg:pt-6 pb-6 space-y-6", children: [_jsx(PageHeader, {}), _jsxs("div", { class: "rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 p-6 text-center text-sm text-gray-600 dark:text-gray-400", children: [_jsx(MetricsBar, {}), _jsx("div", { class: "mt-6", children: "No companies yet." })] })] }));
    return (_jsxs("div", { class: "px-6 pt-4 lg:pt-6 pb-10 space-y-6", children: [_jsx(PageHeader, {}), _jsxs("div", { children: [_jsx(MetricsBar, {}), showLeads && (_jsx(DataGrid, { data: filteredLeads, columns: contactColumns, getRowKey: (l) => l.LeadId, onRowClick: setSelectedLead, getSortComparator: getLeadComparator, scrollable: true, columnVisibilityKey: "orbitalx-lead-columns-workspace", exportConfig: contactExportConfig })), showCompanies && (_jsx(DataGrid, { data: companies, columns: companyColumns, getRowKey: (c) => c.CompanyId || c.CompanyName, getSortComparator: getCompanyComparator, scrollable: true, columnVisibilityKey: "orbitalx-company-columns-workspace", exportConfig: companyExportConfig }))] }), selectedLead && (_jsx(LeadSidePanel, { lead: selectedLead, touchpoints: selectedLead.CampaignTouchpoints, clientCompanyId: clientCompanyId, workspaceId: workspaceId, onClose: () => setSelectedLead(null), onLeadDeleted: (leadId) => {
                    setData(prev => {
                        if (!prev)
                            return prev;
                        return {
                            ...prev,
                            Leads: prev.Leads.filter(l => l.LeadId !== leadId)
                        };
                    });
                    setSelectedLead(null);
                }, onLeadUpdated: (updated) => {
                    setData(prev => {
                        if (!prev)
                            return prev;
                        const nextLeads = prev.Leads.map(l => {
                            if (l.LeadId !== updated.LeadId)
                                return l;
                            const merged = { ...l, ...updated };
                            if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                                const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                                merged.FullName = fn.length > 0 ? fn : merged.FullName;
                            }
                            return merged;
                        });
                        return { ...prev, Leads: nextLeads };
                    });
                    setSelectedLead(curr => {
                        if (!curr || curr.LeadId !== updated.LeadId)
                            return curr;
                        const merged = { ...curr, ...updated };
                        if (updated.FirstName !== undefined || updated.LastName !== undefined) {
                            const fn = [merged.FirstName, merged.LastName].filter(Boolean).join(' ');
                            merged.FullName = fn.length > 0 ? fn : merged.FullName;
                        }
                        return merged;
                    });
                } }))] }));
};
